// Pricing · License Matrix — the rate card every quote validates against.
// Single source of truth: reads GET /pricing/price-book. Currency switch is
// display-time (base currency authoritative). Mirrors portal/pricing.jsx.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { PageHeader, Card, CardHead, Table, Td, Row, Mono, Btn } from '@/components/shared/primitives';
import { Pill, TierPill } from '@/components/shared/badges';
import { AlertBanner } from '@/components/shared/AlertBanner';
import { PERSONA } from '@/lib/persona';
import { getPriceBook, makeFmt, type PriceBook, type Band } from '@/lib/api/pricing';

const SALES = PERSONA.sales.accent;

function CurrencyPicker({ cur, setCur, currencies }: { cur: string; setCur: (c: string) => void; currencies: PriceBook['currencies'] }) {
  return (
    <div style={{ display: 'inline-flex', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
      {Object.keys(currencies).map((k) => (
        <button key={k} onClick={() => setCur(k)} style={{ padding: '7px 12px', border: 'none', background: cur === k ? 'var(--bg-muted)' : 'var(--surface)', color: cur === k ? 'var(--text-primary)' : 'var(--text-secondary)', fontSize: 12.5, fontWeight: cur === k ? 700 : 500, cursor: 'pointer', fontFamily: 'inherit' }}>
          {currencies[k].sym} {k}
        </button>
      ))}
    </div>
  );
}

function VolumeBands({ title, bands, fmt, dp }: { title: string; bands: Band[]; fmt: (n: number, c?: string, dp?: number) => string; dp: number }) {
  return (
    <div>
      <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 8 }}>{title}</div>
      {bands.map((b, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, padding: '6px 0', borderBottom: '1px solid var(--bg-muted)' }}>
          <span style={{ color: 'var(--text-secondary)' }}>{b.from.toLocaleString()}–{b.to >= 1e9 || b.to === null ? '∞' : b.to.toLocaleString()} seats</span>
          <span style={{ fontWeight: 600 }}>{fmt(b.price, undefined, dp)} / seat</span>
        </div>
      ))}
    </div>
  );
}

export function PricingMatrix() {
  const navigate = useNavigate();
  const [pb, setPb] = useState<PriceBook | null>(null);
  const [cur, setCur] = useState('USD');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getPriceBook()
      .then((p) => { setPb(p); setCur(p.baseCurrency || 'USD'); })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load price book'))
      .finally(() => setLoading(false));
  }, []);

  const fmt = useMemo(() => makeFmt(pb?.currencies || { USD: { sym: '$', rate: 1 } }), [pb]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading rate card…</div>;
  if (error || !pb) return <div style={{ padding: 24 }}><AlertBanner kind="error">Unable to load the price book: {error}</AlertBanner></div>;

  return (
    <div>
      <PageHeader
        title="License Matrix"
        subtitle="The rate card every quote validates against — single source of truth"
        actions={
          <>
            <CurrencyPicker cur={cur} setCur={setCur} currencies={pb.currencies} />
            <Btn icon="history" onClick={() => navigate('/sales/pricing-admin')}>Version history</Btn>
            <Btn kind="primary" accent={SALES} icon="file-plus-2" onClick={() => navigate('/sales/quotes/new')}>New Quote</Btn>
          </>
        }
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, fontSize: 12.5, color: 'var(--text-secondary)' }}>
        <Pill bg="var(--ps-pill-blue-bg)" fg="var(--ps-pill-blue-tx)">{pb.version}</Pill>
        <span>Effective {pb.effective}</span>
        <span style={{ color: 'var(--text-muted)' }}>· placeholder numbers — replace with your real price book</span>
        <span style={{ color: 'var(--text-muted)' }}>· platform (reseller) sales priced at wholesale −{pb.wholesaleDiscountPct}%</span>
      </div>

      {/* Tier matrix */}
      <Card style={{ overflow: 'hidden', marginBottom: 16 }}>
        <CardHead title="Tiers" sub="Base fee + included pools; overage & per-seat rates apply beyond included" />
        <Table cols={[{ label: 'Tier' }, { label: 'Base / mo', align: 'right' }, { label: 'Vendors incl.', align: 'right' }, { label: '+ / vendor seat', align: 'right' }, { label: 'Orgs incl.', align: 'right' }, { label: '+ / org seat', align: 'right' }, { label: 'Overage / unit', align: 'right' }]}>
          {pb.tiers.map((t) => (
            <Row key={t.tier}>
              <Td><div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><TierPill tier={t.tier} /><span style={{ fontWeight: 600 }}>{t.name}</span>{t.custom && <Pill bg="var(--ps-pill-purple-bg)" fg="var(--ps-pill-purple-tx)">negotiable</Pill>}</div></Td>
              <Td align="right" style={{ fontWeight: 700 }}>{t.base ? fmt(t.base, cur) : '—'}</Td>
              <Td align="right"><Mono>{t.vendorIncluded.toLocaleString()}</Mono></Td>
              <Td align="right">{t.vendorSeat ? fmt(t.vendorSeat, cur, 2) : '—'}</Td>
              <Td align="right"><Mono>{t.orgIncluded}</Mono></Td>
              <Td align="right">{t.orgSeat ? fmt(t.orgSeat, cur) : '—'}</Td>
              <Td align="right">{t.overage ? fmt(t.overage, cur, 2) : '—'}</Td>
            </Row>
          ))}
        </Table>
      </Card>

      {/* Volume bands */}
      <Card style={{ overflow: 'hidden', marginBottom: 16 }}>
        <CardHead title="Volume pricing" sub="Graduated per-seat rate — marginal price drops as the pool grows (applies above each tier's included seats)" />
        <div style={{ padding: '16px 20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          <VolumeBands title="Vendor seats" bands={pb.vendorBands} fmt={(n, _c, dp) => fmt(n, cur, dp)} dp={2} />
          <VolumeBands title="Org seats" bands={pb.orgBands} fmt={(n, _c, dp) => fmt(n, cur, dp)} dp={0} />
        </div>
      </Card>

      {/* Term discounts */}
      <Card style={{ overflow: 'hidden', marginBottom: 16 }}>
        <CardHead title="Term discounts" sub="Sold annually — longer commitments discount the whole term" />
        <div style={{ padding: '14px 20px', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {pb.terms.map((tm) => (
            <div key={tm.years} style={{ flex: '1 1 120px', border: `1px solid ${tm.discount ? 'var(--ps-pill-green-bg)' : 'var(--border)'}`, borderRadius: 10, padding: '12px 14px', textAlign: 'center', background: tm.discount ? 'var(--ps-pill-green-bg)' : 'var(--surface)' }}>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{tm.years} yr</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: tm.discount ? SALES : 'var(--text-muted)', marginTop: 4 }}>{tm.discount ? `−${Math.round(tm.discount * 100)}%` : 'list'}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Org vs Platform */}
      <Card style={{ overflow: 'hidden', marginBottom: 16 }}>
        <CardHead title="Org vs Platform sale" sub="Direct customers pay retail; reseller partners buy at wholesale and resell downstream" />
        <div style={{ padding: '14px 20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Org license (direct)</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>Billed by us · end-user entitlements</div>
            <div style={{ fontSize: 22, fontWeight: 700, marginTop: 8 }}>Retail</div>
          </div>
          <div style={{ border: '1px solid var(--ps-pill-green-bg)', borderRadius: 10, padding: '14px 16px', background: 'var(--ps-pill-green-bg)' }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4, color: SALES }}>Platform license (reseller)</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>Partner resells &amp; keeps margin · white-label + sub-tenant</div>
            <div style={{ fontSize: 22, fontWeight: 700, marginTop: 8, color: SALES }}>−{pb.wholesaleDiscountPct}% off retail</div>
          </div>
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Card style={{ overflow: 'hidden' }}>
          <CardHead title="Add-on modules" sub="Flat monthly per module" />
          <Table cols={[{ label: 'Module' }, { label: 'Price / mo', align: 'right' }]}>
            {pb.modules.map((m) => (<Row key={m.key}><Td style={{ fontWeight: 600 }}>{m.name}</Td><Td align="right">{fmt(m.price, cur)}</Td></Row>))}
          </Table>
        </Card>
        <Card style={{ overflow: 'hidden' }}>
          <CardHead title="Expansion packs" sub="Pool increments, applied to a live license" />
          <Table cols={[{ label: 'Pack' }, { label: 'Adds', align: 'right' }, { label: 'Price / mo', align: 'right' }]}>
            {pb.packs.map((p) => (<Row key={p.type}><Td style={{ fontWeight: 600 }}>{p.name}</Td><Td align="right"><Mono>+{p.qty}</Mono></Td><Td align="right">{fmt(p.price, cur)}</Td></Row>))}
          </Table>
        </Card>
      </div>
    </div>
  );
}

export default PricingMatrix;
